import type { DeliveryGiantMessageId } from '../../../../../../domain/economy/deliveryGiantMessage';
import {
  DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
  DELIVERY_POPUP_UP_LAYOUT_SCALE_X,
  type DeliveryPopupTail,
} from './deliveryPopupLayout';

/** Figma default message type size. */
export const DELIVERY_POPUP_MESSAGE_FONT_SIZE = 39.37;

/** Figma text nodes — Matias 39.37px, mood-colored outside stroke. */
export type DeliveryPopupMessageSpec = {
  lines: readonly string[];
  layout: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  /** Overrides default Matias size when set. */
  fontSize?: number;
  /** Mood-specific stroke color. */
  stroke: string;
};

/**
 * Offset from default message box to tail-up good_mood text (Figma tops unchanged;
 * left scaled with the 1.2∶1 chrome).
 */
const UP_MESSAGE_OFFSET = {
  left: 37.8 * DELIVERY_POPUP_UP_LAYOUT_SCALE_X - 61.85 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
  top: -30.27,
} as const;

const SINGLE_LINE_LAYOUT = {
  left: 61.85 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
  top: 129.49,
  width: 361.125 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
  height: 47,
} as const;

const DOUBLE_LINE_LAYOUT = {
  left: 61.85 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
  top: 102,
  width: 361.125 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
  height: 94,
} as const;

/** Stroke colors follow popup mood chrome (Figma). */
const MESSAGE_STROKE: Record<DeliveryGiantMessageId, string> = {
  regular: '#3ea576',
  important_day1: '#7a6db2',
  important_day2_plus: '#a05b08',
  urgent_day1: '#bd4655',
  urgent_day2: '#bd4655',
  urgent_day3_plus: '#bd4655',
};

/** GDD v2 — сообщение героя по срочности × дню ожидания. */
export const DELIVERY_POPUP_MESSAGES: Record<
  DeliveryGiantMessageId,
  DeliveryPopupMessageSpec
> = {
  regular: {
    lines: ['Жду любого свободного', 'курьера!'],
    fontSize: DELIVERY_POPUP_MESSAGE_FONT_SIZE * 0.64,
    layout: {
      ...DOUBLE_LINE_LAYOUT,
      top: 110,
      height: 84,
    },
    stroke: MESSAGE_STROKE.regular,
  },
  important_day1: {
    lines: ['Мне очень требуется', 'помощь!'],
    fontSize: DELIVERY_POPUP_MESSAGE_FONT_SIZE * 0.82,
    layout: {
      ...DOUBLE_LINE_LAYOUT,
      top: 108,
      height: 82,
    },
    stroke: MESSAGE_STROKE.important_day1,
  },
  important_day2_plus: {
    lines: ['Жду не дождусь', 'второй день…'],
    fontSize: DELIVERY_POPUP_MESSAGE_FONT_SIZE * 0.8,
    layout: {
      ...DOUBLE_LINE_LAYOUT,
      top: 108,
      height: 82,
    },
    stroke: MESSAGE_STROKE.important_day2_plus,
  },
  urgent_day1: {
    lines: ['Срочно нужна помощь!'],
    fontSize: DELIVERY_POPUP_MESSAGE_FONT_SIZE * 0.8,
    layout: {
      ...SINGLE_LINE_LAYOUT,
      top: 125,
      height: 56,
    },
    stroke: MESSAGE_STROKE.urgent_day1,
  },
  urgent_day2: {
    lines: ['Але, вы меня слышите?', 'Жду второй день.'],
    fontSize: DELIVERY_POPUP_MESSAGE_FONT_SIZE * 0.74,
    layout: {
      ...DOUBLE_LINE_LAYOUT,
      top: 108,
      height: 82,
    },
    stroke: MESSAGE_STROKE.urgent_day2,
  },
  urgent_day3_plus: {
    lines: ['Я пишу гневный отзыв!', 'Нет курьера 3 дня.'],
    fontSize: DELIVERY_POPUP_MESSAGE_FONT_SIZE * 0.72,
    layout: {
      ...DOUBLE_LINE_LAYOUT,
      top: 108,
      height: 82,
    },
    stroke: MESSAGE_STROKE.urgent_day3_plus,
  },
};

export function getDeliveryPopupMessage(
  messageId: DeliveryGiantMessageId,
  tail: DeliveryPopupTail = 'down',
): DeliveryPopupMessageSpec {
  const message = DELIVERY_POPUP_MESSAGES[messageId];
  if (tail !== 'up') {
    return message;
  }

  return {
    ...message,
    layout: {
      ...message.layout,
      left: message.layout.left + UP_MESSAGE_OFFSET.left,
      top: message.layout.top + UP_MESSAGE_OFFSET.top,
    },
  };
}

/** Mood-colored 4px outside stroke via text-shadow (no purple outer ring). */
export function buildDeliveryPopupMessageTextShadow(stroke: string): string {
  return filledStrokeShadow(stroke, 4);
}

function filledStrokeShadow(color: string, radius: number): string {
  const parts: string[] = [];
  for (let x = -radius; x <= radius; x++) {
    for (let y = -radius; y <= radius; y++) {
      if (x * x + y * y <= radius * radius) {
        parts.push(`${x}px ${y}px 0 ${color}`);
      }
    }
  }
  return parts.join(', ');
}
